import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getMyResume, updateMyResume, uploadResumePdf, getResumePdf } from '../../../services/ResumeService';
import { updateJobApplicationStatus } from '../../../services/VacanciesService';
import { useLocation } from 'react-router-dom';
import { useAuthStore, useProfileStore } from '../../../store';
import { Resume } from './Resume';
import { KeySkills } from './KeySkills';
import { LaboralExperiences } from './LaboralExperiences';
import { Education } from './Education';
import { Projects } from './Projects';
import swal from 'sweetalert';
import { LogoSpinner } from '../../../components/LogoSpinner';
import { resolveProfilePhotoUrl } from '../../../helpers/profilePhoto';

var bnr = require('./../../../images/banner/bnr1.jpg');

export function MyResume() {
	const { auth } = useAuthStore();
	const { profile } = useProfileStore();
	const queryClient = useQueryClient();
	const location = useLocation();
	const [applicationIdFromQuery, setApplicationIdFromQuery] = useState(null);
	const [confirmingApplication, setConfirmingApplication] = useState(false);

	// Estado local para el currículum
	const [localResumeData, setLocalResumeData] = useState({
		resumen: '',
		habilidades: [],
		laboralExperiences: [],
		educations: [],
		projects: [],
		pdfUrl: null,
		pdfToken: null
	});

	// Estado para la URL del blob del PDF
	const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
	const pdfBlobUrlRef = useRef(null);

	// Query para obtener el currículum
	const { data: resumeData, isLoading, error } = useQuery(
		'myResume',
		async () => {
			try {
				const response = await getMyResume();
				return response.data;
			} catch (err) {
				// Si el error es 404 o similar (resumen no existe), retornar objeto vacío
				if (err.response?.status === 404 || err.response?.status === 400) {
					return {
						resumen: '',
						habilidades: '',
						laboralExperiences: [],
						educations: [],
						projects: [],
						pdfUrl: null,
						pdfToken: null
					};
				}
				// Para otros errores, lanzar el error para que se maneje normalmente
				throw err;
			}
		},
		{
			retry: (failureCount, error) => {
				// No reintentar si es 404 (resumen no existe)
				if (error.response?.status === 404 || error.response?.status === 400) {
					return false;
				}
				return failureCount < 3;
			}
		}
	);

	// Inicializar estado local cuando se cargan los datos del API
	useEffect(() => {
		if (resumeData) {
			console.log(resumeData);
			setLocalResumeData({
				resumen: resumeData.resumen || '',
				habilidades: resumeData.habilidades || [],
				laboralExperiences: resumeData.laboralExperiences || [],
				educations: resumeData.educations || [],
				projects: resumeData.projects || [],
				pdfUrl: resumeData.pdfUrl || null,
				pdfToken: resumeData.pdfToken || null
			});
		}
	}, [resumeData]);

	// Leer applicationId desde la query string si existe
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const appId = params.get('applicationId') || params.get('postulacionId') || null;
		if (appId) {
			setApplicationIdFromQuery(appId);
		}
	}, [location.search]);

	// Obtener el PDF como blob cuando hay un token disponible
	useEffect(() => {
		if (localResumeData.pdfToken) {
			// Limpiar URL anterior si existe
			if (pdfBlobUrlRef.current) {
				URL.revokeObjectURL(pdfBlobUrlRef.current);
				pdfBlobUrlRef.current = null;
			}

			// Obtener el PDF como blob
			getResumePdf(localResumeData.pdfToken)
				.then((response) => {
					// Verificar que la respuesta sea válida
					if (!response.data) {
						throw new Error('No se recibieron datos del PDF');
					}

					// Obtener el Content-Type de la respuesta o usar 'application/pdf' por defecto
					const contentType = response.headers['content-type'] || 'application/pdf';

					// Si response.data ya es un Blob, usarlo directamente
					// Si no, crear un nuevo Blob desde los datos
					let blob;
					if (response.data instanceof Blob) {
						blob = response.data;
					} else if (response.data instanceof ArrayBuffer) {
						blob = new Blob([response.data], { type: contentType });
					} else if (typeof response.data === 'string') {
						// Si es un string, podría ser base64 o texto, intentar crear blob
						blob = new Blob([response.data], { type: contentType });
					} else {
						// Para otros tipos, intentar crear blob
						blob = new Blob([response.data], { type: contentType });
					}

					// Verificar que el blob tenga datos válidos
					if (blob.size === 0) {
						throw new Error('El PDF recibido está vacío');
					}

					const url = URL.createObjectURL(blob);
					pdfBlobUrlRef.current = url;
					setPdfBlobUrl(url);
				})
				.catch((error) => {
					console.error('Error al cargar el PDF:', error);
					swal('Error', 'No se pudo cargar el PDF. Por favor, intenta de nuevo.', 'error');
					setPdfBlobUrl(null);
				});
		} else {
			// Limpiar URL si no hay token
			if (pdfBlobUrlRef.current) {
				URL.revokeObjectURL(pdfBlobUrlRef.current);
				pdfBlobUrlRef.current = null;
			}
			setPdfBlobUrl(null);
		}

		// Cleanup al desmontar o cambiar token
		return () => {
			if (pdfBlobUrlRef.current) {
				URL.revokeObjectURL(pdfBlobUrlRef.current);
				pdfBlobUrlRef.current = null;
			}
		};
	}, [localResumeData.pdfToken]);

	// Función auxiliar para extraer el mensaje de error
	const getErrorMessage = (error) => {
		if (!error) return 'Error al guardar el currículum';

		const errorData = error.response?.data;
		if (!errorData) return error.message || 'Error al guardar el currículum';

		// message puede ser un array o un string
		if (errorData.message) {
			if (Array.isArray(errorData.message)) {
				return errorData.message.join('. ');
			}
			return errorData.message;
		}

		return error.message || 'Error al guardar el currículum';
	};

	// Mutation para guardar el currículum completo
	const saveResumeMutation = useMutation(
		async (data) => {
			return await updateMyResume(data);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries('myResume');
				swal('Éxito', 'Currículum guardado correctamente', 'success');
			},
			onError: (error) => {
				const errorMessage = getErrorMessage(error);
				swal('Error', errorMessage, 'error');
			},
		}
	);

	// Mutation para subir PDF del currículum
	const uploadPdfMutation = useMutation(
		async (file) => {
			return await uploadResumePdf(file);
		},
		{
			onSuccess: (response) => {
				// Refrescar los datos del resumen para obtener el nuevo pdfToken y pdfUrl
				queryClient.invalidateQueries('myResume');
				swal('Éxito', 'PDF cargado exitosamente', 'success');
			},
			onError: (error) => {
				let errorMessage = 'Error al cargar el PDF';
				if (error.response?.data?.message) {
					errorMessage = error.response.data.message;
				} else if (error.response?.status === 400) {
					errorMessage = 'Archivo inválido o no es PDF';
				} else if (error.response?.status === 401) {
					errorMessage = 'No autorizado';
				} else if (error.response?.status === 403) {
					errorMessage = 'No tiene perfil de usuario';
				}
				swal('Error', errorMessage, 'error');
			},
		}
	);

	// Función para obtener la URL del PDF (solo la URL de blob creada por la petición con axios)
	// Evita usar la URL directa al backend porque el navegador NO envía
	// el header Authorization en peticiones iniciadas por elementos <iframe>/<a>
	const getPdfUrl = () => {
		return pdfBlobUrl || null;
	};

	// Handler para la carga de PDF
	const handlePdfUpload = (event) => {
		const file = event.target.files[0];
		if (!file) return;

		// Validar que sea un archivo PDF
		if (file.type !== 'application/pdf') {
			swal('Error', 'Por favor, selecciona un archivo PDF válido', 'error');
			event.target.value = ''; // Limpiar el input
			return;
		}

		// Validar tamaño del archivo (opcional: máximo 10MB)
		const maxSize = 10 * 1024 * 1024; // 10MB
		if (file.size > maxSize) {
			swal('Error', 'El archivo es demasiado grande. El tamaño máximo es 10MB', 'error');
			event.target.value = '';
			return;
		}

		// Subir el archivo
		uploadPdfMutation.mutate(file);
		event.target.value = ''; // Limpiar el input después de la carga
	};

	// Función para manejar actualizaciones locales desde componentes hijos
	const handleUpdate = (updatedData) => {
		setLocalResumeData(prevData => ({
			...prevData,
			...updatedData
		}));
	};

	// Confirmar la postulación (cambiar estado de la aplicación creada en 'Borrador' a 'Aplicado')
	const handleConfirmApplication = async () => {
		if (!applicationIdFromQuery) return;
		setConfirmingApplication(true);
		try {
			await updateJobApplicationStatus(applicationIdFromQuery, 'Aplicado');
			setApplicationIdFromQuery(null);
			// Remover query param de la URL sin recargar la página
			const cleanPath = window.location.pathname;
			window.history.replaceState({}, document.title, cleanPath);
			queryClient.invalidateQueries('myResume');
			swal('Éxito', 'Postulación confirmada y enviada.', 'success');
			// Redirigir a la lista de vacantes aplicadas
			window.location.href = '/applied-jobs';
		} catch (err) {
			console.error('Error al confirmar la postulación:', err);
			swal('Error', err.response?.data?.message || err.message || 'No se pudo confirmar la postulación', 'error');
		} finally {
			setConfirmingApplication(false);
		}
	};

	// Función auxiliar para limpiar campos del backend de un objeto
	const cleanBackendFields = (obj) => {
		const { id, createdAt, updatedAt, resumeId, ...cleanedObj } = obj;
		return cleanedObj;
	};

	// Función para limpiar arrays de elementos eliminando campos del backend
	const cleanArrayFields = (array) => {
		if (!Array.isArray(array)) return array;
		return array.map(item => cleanBackendFields(item));
	};

	// Función para guardar el currículum completo
	const handleSave = () => {
		console.log(localResumeData);
		// Función para extraer IDs de las habilidades
		const extractSkills = (habilidades) => {
			if (!Array.isArray(habilidades)) return [];
			return habilidades.map(skill => {
				if (typeof skill === 'string') return skill;
				if (skill.tag && skill.tag.nombre) return skill.tag.nombre;
				return '';
			});
		};

		// Preparar datos limpiando campos del backend de los arrays
		const cleanedData = {
			resumen: localResumeData.resumen,
			habilidades: extractSkills(localResumeData.habilidades),
			laboralExperiences: cleanArrayFields(localResumeData.laboralExperiences),
			educations: cleanArrayFields(localResumeData.educations),
			projects: cleanArrayFields(localResumeData.projects)
		};
		saveResumeMutation.mutate(cleanedData);
	};

	// Obtener datos del usuario desde el perfil cargado del backend o desde authStore
	const userProfile = profile?.perfilUsuario || auth?.user?.perfilUsuario || {};
	const profilePhotoUrl = resolveProfilePhotoUrl(profile, auth?.user);
	const userLocation = userProfile.ciudadResidencia
		? (typeof userProfile.ciudadResidencia === 'object' ? userProfile.ciudadResidencia.nombre : userProfile.ciudadResidencia)
		: (userProfile.paisResidencia
			? (typeof userProfile.paisResidencia === 'object' ? userProfile.paisResidencia.nombre : userProfile.paisResidencia)
			: null);

	if (isLoading) {
		return <LogoSpinner />;
	}

	// Función auxiliar para extraer el mensaje de error del query
	const getQueryErrorMessage = (error) => {
		if (!error) return 'Ha ocurrido un error al cargar el currículum. Por favor, intenta de nuevo.';

		const errorData = error.response?.data;
		if (!errorData) return error.message || 'Ha ocurrido un error al cargar el currículum. Por favor, intenta de nuevo.';

		// message puede ser un array o un string
		if (errorData.message) {
			if (Array.isArray(errorData.message)) {
				return errorData.message.join('. ');
			}
			return errorData.message;
		}

		return error.message || 'Ha ocurrido un error al cargar el currículum. Por favor, intenta de nuevo.';
	};

	// Solo mostrar error si no es un 404 (resumen no existe es un estado válido)
	if (error && error.response?.status !== 404 && error.response?.status !== 400) {
		return (
			<div className="page-content">
				<div className="content-block">
					<div className="section-full browse-job content-inner-2">
						<div className="container">
							<div className="job-bx job-profile">
								<div className="alert alert-danger" role="alert">
									<h4 className="alert-heading">Error al cargar el currículum</h4>
									<p>{getQueryErrorMessage(error)}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}


	return (
		<div className="page-content">
			{/* Header con información del usuario */}
			<div className="overlay-black-dark profile-edit p-t50 p-b20" style={{ backgroundImage: "url(" + bnr + ")" }}>
				<div className="container">
					<div className="row">
						<div className="col-lg-8 col-md-7 candidate-info">
							<div className="candidate-detail">
								<div className="canditate-des text-center">
									<Link to={"#"}>
										<img
											alt="Foto de perfil"
											src={userProfile?.fotoPerfil}
											style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', background: '#f0f0f0', border: '2px solid #fff' }}
											// onError={e => { e.target.onerror = null; e.target.src = require("./../../../images/team/pic1.jpg"); }}
										/>
									</Link>
								</div>
								<div className="text-white browse-job text-left">
									<h4 className="m-b0">
										{auth.user?.nombre && auth.user?.apellido
											? `${auth.user.nombre} ${auth.user.apellido}`
											: auth.user?.nombre || 'Usuario'}
									</h4>
									{userProfile.tituloProfesional && (
										<p className="m-b15">{userProfile.tituloProfesional}</p>
									)}
									<ul className="clearfix">
										{userLocation && (
											<li><i className="ti-location-pin"></i> {userLocation}</li>
										)}
										{userProfile.telefono && (
											<li><i className="ti-mobile"></i> {userProfile.telefono}</li>
										)}
										{auth.user?.email && (
											<li><i className="ti-email"></i> {auth.user.email}</li>
										)}
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Alerta de confirmación de postulación */}
			{applicationIdFromQuery && (
				<div className="container m-t30">
					<div className="alert alert-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<div>
							<strong>Has iniciado una postulación (Borrador).</strong>
							<p className="mb-0">Completa o sube tu currículum y confirma la postulación cuando estés listo.</p>
						</div>
						<div>
							<button
								className="site-button"
								onClick={handleConfirmApplication}
								disabled={confirmingApplication}
							>
								{confirmingApplication ? 'Confirmando...' : 'Confirmar Postulación'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Contenido principal */}
			<div className="content-block">
				<div className="section-full browse-job content-inner-2">
					<div className="container">
						<div className="row">
							<div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
								<div className=" m-b30 " style={{
									display: 'flex', flexDirection: 'row', justifyContent: 'space-between',
									alignItems: 'center'
								}}>
									<div className="d-flex flex-row " style={{ gap: '20px', alignItems: 'center' }}>
										<Link to="/profile" className="site-button right-arrow button-sm" >
											<i className="fa fa-arrow-left m-r5" style={{}}></i> Atrás
										</Link>
										<h5 className="font-weight-700  text-uppercase mt-3">Mi Currículum</h5>
									</div>
									<div className="" >
										<button
											type="button"
											className="site-button button-sm m-r10"
											onClick={handleSave}
											disabled={saveResumeMutation.isLoading}
											style={{ fontSize: '1rem', padding: '0.6em 2em', minWidth: '135px' }}
										>
											{saveResumeMutation.isLoading ? (
												<>
													<span className="spinner-border spinner-border-sm m-r5" role="status" aria-hidden="true"></span>
													Guardando...
												</>
											) : (
												<>
													<i className="fa fa-save m-r5"></i> Guardar
												</>
											)}
										</button>

									</div>
								</div>
								<Resume
									resumen={localResumeData.resumen || ''}
									onUpdate={(resumen) => handleUpdate({ resumen })}
								/>
								<KeySkills
									habilidades={localResumeData.habilidades || []}
									onUpdate={(habilidades) => handleUpdate({ habilidades })}
								/>
								<LaboralExperiences
									laboralExperiences={localResumeData.laboralExperiences || []}
									onUpdate={(laboralExperiences) => handleUpdate({ laboralExperiences })}
								/>
								<Education
									educations={localResumeData.educations || []}
									onUpdate={(educations) => handleUpdate({ educations })}
								/>
								<Projects
									projects={localResumeData.projects || []}
									onUpdate={(projects) => handleUpdate({ projects })}
								/>
								{/* Sección de anexar CV */}
								<div id="attach_resume_bx" className="job-bx bg-white m-b30">
									<h5 className="m-b10">Anexar CV</h5>
									<p>El currículum es el documento más importante que buscan los reclutadores. Los reclutadores generalmente no miran perfiles sin currículums.</p>

									<div className="row">
										<div className="col-lg-12 col-md-12">
											<div className="form-group">
												<label htmlFor="pdfFileInput" className="form-label">
													{localResumeData.pdfToken ? 'Reemplazar PDF del currículum' : 'Cargar PDF del currículum'}
												</label>
												<div className="d-flex align-items-center" style={{ gap: '10px', marginBottom: '20px' }}>
													<input
														type="file"
														className="form-control"
														id="pdfFileInput"
														accept="application/pdf"
														onChange={handlePdfUpload}
														disabled={uploadPdfMutation.isLoading}
														style={{ maxWidth: '300px' }}
													/>
													{uploadPdfMutation.isLoading && (
														<div className="d-flex align-items-center">
															<span className="spinner-border spinner-border-sm m-r5" role="status" aria-hidden="true"></span>
															<span>Subiendo...</span>
														</div>
													)}
												</div>
												<small className="form-text text-muted">
													Solo se permiten archivos PDF. Tamaño máximo: 10MB
												</small>
											</div>
										</div>
									</div>

									{/* Visualización del PDF */}
									{localResumeData.pdfToken && pdfBlobUrl ? (
										<div className="mt-4">
											<h6 className="m-b10">Vista previa del PDF</h6>
											<div style={{
												border: '1px solid #ddd',
												borderRadius: '4px',
												overflow: 'hidden',
												height: '600px',
												width: '100%'
											}}>
												<iframe
													src={pdfBlobUrl}
													title="Vista previa del currículum PDF"
													style={{
														width: '100%',
														height: '100%',
														border: 'none'
													}}
												/>
											</div>
											<div className="mt-3">
												<button
													type="button"
													className="site-button button-sm"
													onClick={() => window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer')}
												>
													<i className="fa fa-external-link m-r5"></i>
													Abrir PDF en nueva pestaña
												</button>
											</div>
										</div>
									) : (
										localResumeData.pdfToken && (
											<div className="mt-4">
												<h6 className="m-b10">Vista previa del PDF</h6>
												<div style={{ padding: '1rem' }}>
													<div className="spinner-border text-primary" role="status">
														<span className="sr-only">Cargando...</span>
													</div>
													<span style={{ marginLeft: '10px' }}>Cargando PDF...</span>
												</div>
											</div>
										)
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}